'use client';

import { useState } from 'react';
import useVimeoPlayer from './use-vimeo-player';
import SoundOff from './icons/SoundOff';
import SoundOn from './icons/SoundOn';
import Play from './icons/Play';

// Ported from helix-web's
// components/blocks/Media/videoCarousel/VimeoOverlayPlayer.js, with the
// Product-dependent ProductDrawer/PriceTag/Badge pieces removed entirely (per
// decision to drop Product from this block). The manual close button is also
// gone — this renders inside birch's shared modal (components/ModalContext.js),
// which already provides a close button, backdrop-click-to-close, Escape, and
// focus trapping, so there's no `hideModal` prop to wire up here.
export default function VimeoOverlayPlayer({ video }) {
  const [message, setMessage] = useState(null);

  // Extract video ID from Vimeo URL and construct proper player URL
  const getVimeoPlayerUrl = (url) => {
    // Handle various Vimeo URL formats:
    // https://vimeo.com/123456789
    // https://vimeo.com/123456789?foo=bar
    // https://player.vimeo.com/video/123456789
    const videoIdMatch = url.match(
      /(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/,
    );
    const videoId = videoIdMatch ? videoIdMatch[1] : null;

    if (!videoId) {
      console.error('Could not extract Vimeo video ID from URL:', url);

      return url;
    }

    return `https://player.vimeo.com/video/${videoId}`;
  };

  const videoSrc = `${getVimeoPlayerUrl(video.video_url)}?controls=0&autoplay=1&loop=1&muted=1`;

  const { iframeRef, playerRef, isLoaded, isMuted, isPaused, isBuffering } =
    useVimeoPlayer(video);

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={`relative mx-auto aspect-[9/16] h-[80vh] w-auto max-w-full overflow-hidden rounded-2xl bg-black ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100`}
    >
      <iframe
        src={videoSrc}
        ref={iframeRef}
        className="absolute inset-0 h-full w-full"
        title="Vimeo video player"
        allow="autoplay; fullscreen"
        allowFullScreen
      />
      <aside
        className={`absolute inset-0 flex flex-col items-stretch justify-between ${isPaused ? 'bg-black/35' : 'bg-black/0'}`}
      >
        <PlayButtonOverlay
          playerRef={playerRef}
          isPaused={isPaused}
          isBuffering={isBuffering}
          videoUrl={video.video_url}
          setMessage={setMessage}
        />

        {isBuffering && <BufferingScreen />}

        <SocialLinkAndActions
          video={video}
          playerRef={playerRef}
          isMuted={isMuted}
        />

        {message && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="pointer-events-auto max-w-61.25 bg-black/80 px-3 py-1 text-base text-white">
              {message}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

/**
 * A very large play button that allows the entire screen to be clicked to play
 * or pause the video. (Order other important elements on top of this if important.)
 */
const PlayButtonOverlay = ({
  playerRef,
  isPaused,
  isBuffering,
  videoUrl,
  setMessage,
}) => {
  const togglePlayback = async (playerRef, setMessage) => {
    if (playerRef.current) {
      const paused = await playerRef.current.getPaused();
      if (paused) {
        return playerRef.current.play().catch(() => {
          setMessage(
            <>
              <p>Looks like your browser might be blocking playback.</p>
              <p>
                Try watching with the sound muted, or{' '}
                <a
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={videoUrl}
                >
                  watch it directly on Vimeo.
                </a>
              </p>
            </>,
          );
          setTimeout(() => {
            setMessage(null);
          }, 3000);
        });
      } else {
        return playerRef.current.pause();
      }
    }
  };

  return (
    <button
      type="button"
      className={`group absolute inset-0 flex items-center justify-center p-5 ${isPaused && !isBuffering ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100`}
      onClick={() => togglePlayback(playerRef, setMessage)}
      aria-label={isPaused ? 'Play video' : 'Pause video'}
    >
      <Play />
    </button>
  );
};

const BufferingScreen = () => {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-white">
      <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-b-transparent" />
    </div>
  );
};

const SocialLinkAndActions = ({ video, playerRef, isMuted }) => {
  return (
    <div className="relative flex flex-row-reverse items-start justify-between bg-linear-to-t from-transparent to-[rgba(0,0,0,0.25)] p-3 text-white">
      <button
        type="button"
        className="group"
        onClick={() => {
          if (playerRef.current) {
            playerRef.current.getMuted().then((muted) => {
              if (muted) {
                playerRef.current.setMuted(false);
              } else {
                playerRef.current.setMuted(true);
              }
            });
          }
        }}
        aria-label="Toggle mute"
      >
        {isMuted ? (
          <SoundOff className="h-8 w-10" />
        ) : (
          <SoundOn className="h-8 w-10" />
        )}
      </button>
      {video.social_link?.href && video.social_link?.title && (
        <a className="px-1 text-base hover:underline" href={video.social_link.href}>
          {video.social_link.title}
        </a>
      )}
    </div>
  );
};
